import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Modal from '../../components/ui/Modal';
import { PlusIcon, SearchIcon, FilterIcon, DownloadIcon, EditIcon, Trash2Icon, PowerIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, TagIcon, PercentIcon, XIcon } from 'lucide-react';
// Mock data for banks
const mockBanks = [{
  id: 'bank1',
  name: 'HBL'
}, {
  id: 'bank2',
  name: 'UBL'
}, {
  id: 'bank3',
  name: 'MCB'
}, {
  id: 'bank4',
  name: 'Allied Bank'
}, {
  id: 'bank5',
  name: 'Bank Alfalah'
}, {
  id: 'bank6',
  name: 'Meezan Bank'
}, {
  id: 'bank7',
  name: 'Faysal Bank'
}, {
  id: 'bank8',
  name: 'Standard Chartered'
}];
// Mock data for MCC codes
const mockMCCCodes = [{
  code: '5411',
  name: 'Grocery Stores'
}, {
  code: '5812',
  name: 'Restaurants'
}, {
  code: '5912',
  name: 'Drug Stores'
}, {
  code: '5541',
  name: 'Service Stations'
}, {
  code: '5311',
  name: 'Department Stores'
}, {
  code: '5999',
  name: 'Miscellaneous Retail'
}, {
  code: '5814',
  name: 'Fast Food Restaurants'
}, {
  code: '5732',
  name: 'Electronics Stores'
}];
// Mock data for merchants
const mockMerchants = [{
  id: 'M001',
  name: 'Metro Cash & Carry',
  category: 'Grocery'
}, {
  id: 'M002',
  name: 'Carrefour',
  category: 'Retail'
}, {
  id: 'M003',
  name: "McDonald's Pakistan",
  category: 'Restaurant'
}, {
  id: 'M004',
  name: 'KFC Pakistan',
  category: 'Restaurant'
}, {
  id: 'M005',
  name: 'Daraz',
  category: 'E-commerce'
}, {
  id: 'M006',
  name: 'Imtiaz Super Market',
  category: 'Grocery'
}, {
  id: 'M007',
  name: 'Sapphire Retail',
  category: 'Fashion'
}, {
  id: 'M008',
  name: 'Gul Ahmed',
  category: 'Fashion'
}];
interface Discount {
  id: string;
  name: string;
  description: string;
  scheme: string[];
  bank: string[];
  allBanks: boolean;
  channel: string;
  binRange: string;
  cardType: string[];
  mccCodes: string[];
  allMCCs: boolean;
  merchants: string[];
  allMerchants: boolean;
  discountType: string;
  discountValue: number;
  maxCap: number;
  minTransaction: number;
  maxTransactionsPerCustomer: number;
  maxDiscountPerCustomer: number;
  effectiveDate: string;
  endDate: string;
  status: string;
}
// Generate mock discounts
const generateMockDiscounts = (): Discount[] => {
  return [{
    id: 'DISC001',
    name: 'Summer Sale 2024',
    description: 'Special summer discount for all Visa cardholders',
    scheme: ['Visa'],
    bank: ['HBL', 'UBL'],
    allBanks: false,
    channel: 'Card',
    binRange: '450000, 450999',
    cardType: ['Credit', 'Debit'],
    mccCodes: ['5411', '5812'],
    allMCCs: false,
    merchants: ['M001', 'M002'],
    allMerchants: false,
    discountType: 'Percentage',
    discountValue: 20,
    maxCap: 4000,
    minTransaction: 1000,
    maxTransactionsPerCustomer: 5,
    maxDiscountPerCustomer: 20000,
    effectiveDate: '2024-06-01',
    endDate: '2024-08-31',
    status: 'Active'
  }, {
    id: 'DISC002',
    name: 'QR Code Promotion',
    description: 'Flat discount on QR code payments',
    scheme: ['UPI'],
    bank: [],
    allBanks: true,
    channel: 'QR',
    binRange: '',
    cardType: [],
    mccCodes: [],
    allMCCs: true,
    merchants: [],
    allMerchants: true,
    discountType: 'Flat',
    discountValue: 500,
    maxCap: 500,
    minTransaction: 2000,
    maxTransactionsPerCustomer: 3,
    maxDiscountPerCustomer: 1500,
    effectiveDate: '2024-05-15',
    endDate: '2024-12-31',
    status: 'Active'
  }, {
    id: 'DISC003',
    name: 'Mastercard Weekend Deal',
    description: 'Weekend special for Mastercard users',
    scheme: ['Mastercard'],
    bank: ['MCB', 'Allied Bank'],
    allBanks: false,
    channel: 'Card',
    binRange: '51000000, 55999999',
    cardType: ['Credit'],
    mccCodes: ['5812', '5541'],
    allMCCs: false,
    merchants: ['M003', 'M004'],
    allMerchants: false,
    discountType: 'Percentage',
    discountValue: 15,
    maxCap: 3000,
    minTransaction: 1500,
    maxTransactionsPerCustomer: 10,
    maxDiscountPerCustomer: 30000,
    effectiveDate: '2024-07-01',
    endDate: '2024-07-31',
    status: 'Scheduled'
  }];
};
const DiscountManagement: React.FC = () => {
  const [discounts, setDiscounts] = useState<Discount[]>(generateMockDiscounts());
  const [filteredDiscounts, setFilteredDiscounts] = useState<Discount[]>(generateMockDiscounts());
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [schemeFilter, setSchemeFilter] = useState<string[]>([]);
  const [channelFilter, setChannelFilter] = useState('');
  const [bankFilter, setBankFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  // Modal states
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<Discount | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // Form states
  const [formData, setFormData] = useState<Discount>({
    id: '',
    name: '',
    description: '',
    scheme: [],
    bank: [],
    allBanks: true,
    channel: 'Card',
    binRange: '',
    cardType: [],
    mccCodes: [],
    allMCCs: true,
    merchants: [],
    allMerchants: true,
    discountType: 'Percentage',
    discountValue: 0,
    maxCap: 0,
    minTransaction: 0,
    maxTransactionsPerCustomer: 0,
    maxDiscountPerCustomer: 0,
    effectiveDate: '',
    endDate: '',
    status: 'Active'
  });
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Sorting
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  // Dropdown states
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showMCCDropdown, setShowMCCDropdown] = useState(false);
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);
  // Apply filters
  useEffect(() => {
    let result = discounts;
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(d => d.name.toLowerCase().includes(term) || d.scheme.some(s => s.toLowerCase().includes(term)) || d.bank.some(b => b.toLowerCase().includes(term)) || d.binRange.toLowerCase().includes(term));
    }
    // Scheme filter
    if (schemeFilter.length > 0) {
      result = result.filter(d => d.scheme.some(s => schemeFilter.includes(s)));
    }
    // Channel filter
    if (channelFilter) {
      result = result.filter(d => d.channel === channelFilter || d.channel === 'Both');
    }
    // Bank filter
    if (bankFilter.length > 0) {
      result = result.filter(d => d.allBanks || d.bank.some(b => bankFilter.includes(b)));
    }
    // Status filter
    if (statusFilter) {
      result = result.filter(d => d.status === statusFilter);
    }
    // Date range filter
    if (dateFromFilter) {
      result = result.filter(d => new Date(d.effectiveDate) >= new Date(dateFromFilter));
    }
    if (dateToFilter) {
      result = result.filter(d => new Date(d.endDate) <= new Date(dateToFilter));
    }
    // Sorting
    result.sort((a, b) => {
      const aValue = a[sortField as keyof Discount];
      const bValue = b[sortField as keyof Discount];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return 0;
    });
    setFilteredDiscounts(result);
    setCurrentPage(1);
  }, [searchTerm, schemeFilter, channelFilter, bankFilter, statusFilter, dateFromFilter, dateToFilter, sortField, sortDirection, discounts]);
  const resetFilters = () => {
    setSearchTerm('');
    setSchemeFilter([]);
    setChannelFilter('');
    setBankFilter([]);
    setStatusFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const handleAddNew = () => {
    setEditingDiscount(null);
    setFormData({
      id: '',
      name: '',
      description: '',
      scheme: [],
      bank: [],
      allBanks: true,
      channel: 'Card',
      binRange: '',
      cardType: [],
      mccCodes: [],
      allMCCs: true,
      merchants: [],
      allMerchants: true,
      discountType: 'Percentage',
      discountValue: 0,
      maxCap: 0,
      minTransaction: 0,
      maxTransactionsPerCustomer: 0,
      maxDiscountPerCustomer: 0,
      effectiveDate: '',
      endDate: '',
      status: 'Active'
    });
    setErrors({});
    setShowPreview(false);
    setShowAddEditModal(true);
  };
  const handleEdit = (discount: Discount) => {
    setEditingDiscount(discount);
    setFormData(discount);
    setErrors({});
    setShowPreview(false);
    setShowAddEditModal(true);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this discount?')) {
      setDiscounts(discounts.filter(d => d.id !== id));
    }
  };
  const handleDeactivate = (id: string) => {
    setDiscounts(discounts.map(d => d.id === id ? {
      ...d,
      status: d.status === 'Active' ? 'Inactive' : 'Active'
    } : d));
  };
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Discount name is required';
    }
    if (!formData.effectiveDate) {
      newErrors.effectiveDate = 'Effective date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (formData.effectiveDate && formData.endDate) {
      if (new Date(formData.endDate) <= new Date(formData.effectiveDate)) {
        newErrors.endDate = 'End date must be after effective date';
      }
    }
    if (formData.discountType === 'Percentage') {
      if (formData.discountValue <= 0 || formData.discountValue > 100) {
        newErrors.discountValue = 'Percentage must be between 1 and 100';
      }
      if (formData.maxCap <= 0) {
        newErrors.maxCap = 'Max cap is mandatory for percentage discount';
      }
    } else {
      if (formData.discountValue <= 0) {
        newErrors.discountValue = 'Discount value must be greater than 0';
      }
    }
    if (formData.binRange && formData.channel === 'Card') {
      const bins = formData.binRange.split(',').map(bin => bin.trim());
      const binPattern = /^\d{6}$|^\d{8}$/;
      const invalidBins = bins.filter(bin => !binPattern.test(bin));
      if (invalidBins.length > 0) {
        newErrors.binRange = 'Each BIN must be exactly 6 or 8 digits';
      }
    }
    if (formData.scheme.length === 0) {
      newErrors.scheme = 'At least one scheme must be selected';
    }
    if (!formData.allBanks && formData.bank.length === 0) {
      newErrors.bank = 'At least one bank must be selected';
    }
    if (!formData.allMCCs && formData.mccCodes.length === 0) {
      newErrors.mccCodes = 'At least one MCC must be selected';
    }
    if (!formData.allMerchants && formData.merchants.length === 0) {
      newErrors.merchants = 'At least one merchant must be selected';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    if (editingDiscount) {
      setDiscounts(discounts.map(d => d.id === editingDiscount.id ? formData : d));
    } else {
      const newDiscount = {
        ...formData,
        id: `DISC${String(discounts.length + 1).padStart(3, '0')}`
      };
      setDiscounts([...discounts, newDiscount]);
    }
    setShowAddEditModal(false);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" data-id="element-2519">
            <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-2520" />
            Active
          </span>;
      case 'Expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" data-id="element-2521">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-2522" />
            Expired
          </span>;
      case 'Scheduled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-2523">
            <ClockIcon className="w-3 h-3 mr-1" data-id="element-2524" />
            Scheduled
          </span>;
      case 'Inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" data-id="element-2525">
            <XCircleIcon className="w-3 h-3 mr-1" data-id="element-2526" />
            Inactive
          </span>;
      default:
        return <span data-id="element-2527">{status}</span>;
    }
  };
  const formatDiscountDisplay = (discount: Discount) => {
    if (discount.discountType === 'Percentage') {
      return `${discount.discountValue}% (Max ${formatCurrency(discount.maxCap)})`;
    }
    return formatCurrency(discount.discountValue);
  };
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredDiscounts.slice(startIndex, startIndex + itemsPerPage);
  };
  const totalPages = Math.ceil(filteredDiscounts.length / itemsPerPage);
  // Multi-select dropdown component
  const MultiSelectDropdown = ({
    label,
    options,
    selectedValues,
    onChange,
    isOpen,
    setIsOpen,
    error,
    displayKey = 'name',
    valueKey = 'id'
  }: {
    label: string;
    options: any[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    error?: string;
    displayKey?: string;
    valueKey?: string;
  }) => {
    const toggleOption = (value: string) => {
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter(v => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    };
    const removeOption = (value: string) => {
      onChange(selectedValues.filter(v => v !== value));
    };
    return <div className="relative" data-id="element-2528">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2529">
          {label}
        </label>
        <div className={`w-full rounded-md border ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm cursor-pointer min-h-[38px]`} onClick={() => setIsOpen(!isOpen)} data-id="element-2530">
          {selectedValues.length === 0 ? <span className="text-gray-400" data-id="element-2531">
              Select {label.toLowerCase()}...
            </span> : <div className="flex flex-wrap gap-1" data-id="element-2532">
              {selectedValues.map(value => {
            const option = options.find(opt => opt[valueKey] === value);
            return <span key={value} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-2533">
                    {option ? option[displayKey] : value}
                    <button onClick={e => {
                e.stopPropagation();
                removeOption(value);
              }} className="ml-1 hover:text-blue-900 dark:hover:text-blue-200" data-id="element-2534">
                      <XIcon className="h-3 w-3" data-id="element-2535" />
                    </button>
                  </span>;
          })}
            </div>}
        </div>
        {isOpen && <>
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-2536" />
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto" data-id="element-2537">
              {options.map(option => <div key={option[valueKey]} className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center" onClick={() => toggleOption(option[valueKey])} data-id="element-2538">
                  <input type="checkbox" checked={selectedValues.includes(option[valueKey])} onChange={() => {}} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2" data-id="element-2539" />
                  <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2540">
                    {option[displayKey]}
                    {option.category && <span className="ml-2 text-xs text-gray-500" data-id="element-2541">
                        ({option.category})
                      </span>}
                  </span>
                </div>)}
            </div>
          </>}
        {error && <p className="mt-1 text-xs text-red-600" data-id="element-2542">{error}</p>}
      </div>;
  };
  return <div className="space-y-6" data-id="element-2543">
      <div data-id="element-2544">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2545">
          Discount Management
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-2546">
          Manage card and QR-based merchant transaction discounts
        </p>
      </div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4" data-id="element-2547">
        <div className="relative flex-1" data-id="element-2548">
          <input type="text" placeholder="Search by name, scheme, BIN, or bank..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2549" />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2550">
            <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-2551" />
          </div>
        </div>
        <div className="flex gap-2" data-id="element-2552">
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2553">
            <FilterIcon className="h-4 w-4 mr-2" data-id="element-2554" />
            Filters
          </button>
          <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2555">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-2556" />
            Add New Discount
          </button>
        </div>
      </div>
      {/* Filters Panel */}
      {showFilters && <Card data-id="element-2557">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-2558">
            <div data-id="element-2559">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2560">
                Scheme
              </label>
              <div className="space-y-2" data-id="element-2561">
                {['Visa', 'Mastercard', 'PayPak', 'UPI'].map(scheme => <label key={scheme} className="flex items-center" data-id="element-2562">
                    <input type="checkbox" checked={schemeFilter.includes(scheme)} onChange={e => {
                if (e.target.checked) {
                  setSchemeFilter([...schemeFilter, scheme]);
                } else {
                  setSchemeFilter(schemeFilter.filter(s => s !== scheme));
                }
              }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2563" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2564">
                      {scheme}
                    </span>
                  </label>)}
              </div>
            </div>
            <div data-id="element-2565">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2566">
                Channel
              </label>
              <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2567">
                <option value="" data-id="element-2568">All Channels</option>
                <option value="Card" data-id="element-2569">Card</option>
                <option value="QR" data-id="element-2570">QR</option>
                <option value="Both" data-id="element-2571">Both</option>
              </select>
            </div>
            <div data-id="element-2572">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2573">
                Bank
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto" data-id="element-2574">
                {mockBanks.map(bank => <label key={bank.id} className="flex items-center" data-id="element-2575">
                    <input type="checkbox" checked={bankFilter.includes(bank.name)} onChange={e => {
                if (e.target.checked) {
                  setBankFilter([...bankFilter, bank.name]);
                } else {
                  setBankFilter(bankFilter.filter(b => b !== bank.name));
                }
              }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2576" />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2577">
                      {bank.name}
                    </span>
                  </label>)}
              </div>
            </div>
            <div data-id="element-2578">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2579">
                Status
              </label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2580">
                <option value="" data-id="element-2581">All Statuses</option>
                <option value="Active" data-id="element-2582">Active</option>
                <option value="Expired" data-id="element-2583">Expired</option>
                <option value="Scheduled" data-id="element-2584">Scheduled</option>
                <option value="Inactive" data-id="element-2585">Inactive</option>
              </select>
            </div>
            <div data-id="element-2586">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2587">
                Effective Date From
              </label>
              <div className="relative" data-id="element-2588">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2589">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2590" />
                </div>
                <input type="date" value={dateFromFilter} onChange={e => setDateFromFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2591" />
              </div>
            </div>
            <div data-id="element-2592">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2593">
                End Date To
              </label>
              <div className="relative" data-id="element-2594">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2595">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2596" />
                </div>
                <input type="date" value={dateToFilter} onChange={e => setDateToFilter(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2597" />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3" data-id="element-2598">
            <button onClick={resetFilters} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2599">
              Reset
            </button>
          </div>
        </Card>}
      {/* Discounts Table */}
      <Card data-id="element-2600">
        <div className="mb-4 flex items-center justify-between" data-id="element-2601">
          <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2602">
            Showing {filteredDiscounts.length} discount(s)
          </div>
          <button onClick={() => console.log('Export')} className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2603">
            <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2604" />
            Export
          </button>
        </div>
        {getCurrentPageData().length > 0 ? <>
            <div className="overflow-x-auto" data-id="element-2605">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2606">
                <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-2607">
                  <tr data-id="element-2608">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('name')} data-id="element-2609">
                      <div className="flex items-center" data-id="element-2610">
                        Discount Name
                        {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" data-id="element-2611" /> : <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-2612" />)}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2613">
                      Scheme
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2614">
                      Bank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2615">
                      Channel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2616">
                      BIN Range
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2617">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2618">
                      Effective Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2619">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2620">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-2621">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2622">
                  {getCurrentPageData().map(discount => <tr key={discount.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-2623">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2624">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2625">
                          {discount.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2626">
                          {discount.description.substring(0, 50)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2627">
                        {discount.scheme.join(', ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2628">
                        {discount.bank.length > 2 ? `${discount.bank.slice(0, 2).join(', ')}...` : discount.bank.join(', ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2629">
                        {discount.channel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2630">
                        {discount.binRange || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2631">
                        <div className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2632">
                          {formatDiscountDisplay(discount)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2633">
                          {discount.discountType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2634">
                        {formatDate(discount.effectiveDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-2635">
                        {formatDate(discount.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2636">
                        {getStatusBadge(discount.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2637">
                        <div className="flex justify-end space-x-2" data-id="element-2638">
                          <button onClick={() => handleEdit(discount)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="Edit" data-id="element-2639">
                            <EditIcon className="h-4 w-4" data-id="element-2640" />
                          </button>
                          <button onClick={() => handleDeactivate(discount.id)} className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300" title={discount.status === 'Active' ? 'Deactivate' : 'Activate'} data-id="element-2641">
                            <PowerIcon className="h-4 w-4" data-id="element-2642" />
                          </button>
                          <button onClick={() => handleDelete(discount.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Delete" data-id="element-2643">
                            <Trash2Icon className="h-4 w-4" data-id="element-2644" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && <div className="mt-4 flex items-center justify-between" data-id="element-2645">
                <div className="text-sm text-gray-700 dark:text-gray-300" data-id="element-2646">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredDiscounts.length)}{' '}
                  of {filteredDiscounts.length} results
                </div>
                <div className="flex space-x-2" data-id="element-2647">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50" data-id="element-2648">
                    Previous
                  </button>
                  {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded-md text-sm ${currentPage === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-2649">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50" data-id="element-2650">
                    Next
                  </button>
                </div>
              </div>}
          </> : <div className="text-center py-12" data-id="element-2651">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-2652" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white" data-id="element-2653">
              No discounts found
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-2654">
              Click "Add New Discount" to create one.
            </p>
          </div>}
      </Card>
      {/* Add/Edit Modal */}
      <Modal isOpen={showAddEditModal} onClose={() => setShowAddEditModal(false)} title={editingDiscount ? 'Edit Discount' : 'Add New Discount'} size="xl" data-id="element-2655">
        <div className="space-y-6" data-id="element-2656">
          {!showPreview ? <>
              {/* General Details */}
              <div data-id="element-2657">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-2658">
                  General Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2659">
                  <div className="md:col-span-2" data-id="element-2660">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2661">
                      Discount Name *
                    </label>
                    <input type="text" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className={`w-full rounded-md border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2662" />
                    {errors.name && <p className="mt-1 text-xs text-red-600" data-id="element-2663">{errors.name}</p>}
                  </div>
                  <div className="md:col-span-2" data-id="element-2664">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2665">
                      Description
                    </label>
                    <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} rows={3} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2666" />
                  </div>
                  <div data-id="element-2667">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2668">
                      Effective Date *
                    </label>
                    <input type="date" value={formData.effectiveDate} onChange={e => setFormData({
                  ...formData,
                  effectiveDate: e.target.value
                })} className={`w-full rounded-md border ${errors.effectiveDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2669" />
                    {errors.effectiveDate && <p className="mt-1 text-xs text-red-600" data-id="element-2670">
                        {errors.effectiveDate}
                      </p>}
                  </div>
                  <div data-id="element-2671">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2672">
                      End Date *
                    </label>
                    <input type="date" value={formData.endDate} onChange={e => setFormData({
                  ...formData,
                  endDate: e.target.value
                })} className={`w-full rounded-md border ${errors.endDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2673" />
                    {errors.endDate && <p className="mt-1 text-xs text-red-600" data-id="element-2674">
                        {errors.endDate}
                      </p>}
                  </div>
                </div>
              </div>
              {/* Eligibility & Targeting */}
              <div data-id="element-2675">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-2676">
                  Eligibility & Targeting
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2677">
                  <div data-id="element-2678">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2679">
                      Applicable Scheme *
                    </label>
                    <div className="space-y-2" data-id="element-2680">
                      {['Visa', 'Mastercard', 'PayPak', 'UPI'].map(scheme => <label key={scheme} className="flex items-center" data-id="element-2681">
                          <input type="checkbox" checked={formData.scheme.includes(scheme)} onChange={e => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          scheme: [...formData.scheme, scheme]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          scheme: formData.scheme.filter(s => s !== scheme)
                        });
                      }
                    }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2682" />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2683">
                            {scheme}
                          </span>
                        </label>)}
                    </div>
                    {errors.scheme && <p className="mt-1 text-xs text-red-600" data-id="element-2684">
                        {errors.scheme}
                      </p>}
                  </div>
                  <div data-id="element-2685">
                    <label className="flex items-center mb-2" data-id="element-2686">
                      <input type="checkbox" checked={formData.allBanks} onChange={e => {
                    setFormData({
                      ...formData,
                      allBanks: e.target.checked,
                      bank: e.target.checked ? [] : formData.bank
                    });
                    setShowBankDropdown(false);
                  }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2687" />
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2688">
                        All Banks *
                      </span>
                    </label>
                    {!formData.allBanks && <MultiSelectDropdown label="Select Banks" options={mockBanks} selectedValues={formData.bank} onChange={values => setFormData({
                  ...formData,
                  bank: values
                })} isOpen={showBankDropdown} setIsOpen={setShowBankDropdown} error={errors.bank} displayKey="name" valueKey="name" data-id="element-2689" />}
                  </div>
                  <div data-id="element-2690">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2691">
                      Channel Type
                    </label>
                    <select value={formData.channel} onChange={e => setFormData({
                  ...formData,
                  channel: e.target.value
                })} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2692">
                      <option value="Card" data-id="element-2693">Card</option>
                      <option value="QR" data-id="element-2694">QR</option>
                      <option value="Both" data-id="element-2695">Both</option>
                    </select>
                  </div>
                  <div data-id="element-2696">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2697">
                      BIN (6 or 8 digits, comma separated)
                    </label>
                    <input type="text" value={formData.binRange} onChange={e => setFormData({
                  ...formData,
                  binRange: e.target.value
                })} placeholder="e.g., 450000, 450999, 51000000" className={`w-full rounded-md border ${errors.binRange ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2698" />
                    {errors.binRange && <p className="mt-1 text-xs text-red-600" data-id="element-2699">
                        {errors.binRange}
                      </p>}
                    <p className="mt-1 text-xs text-gray-500" data-id="element-2700">
                      Enter multiple BINs separated by commas
                    </p>
                  </div>
                  <div data-id="element-2701">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2702">
                      Card Type
                    </label>
                    <div className="space-y-2" data-id="element-2703">
                      {['Credit', 'Debit', 'Prepaid'].map(type => <label key={type} className="flex items-center" data-id="element-2704">
                          <input type="checkbox" checked={formData.cardType.includes(type)} onChange={e => {
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          cardType: [...formData.cardType, type]
                        });
                      } else {
                        setFormData({
                          ...formData,
                          cardType: formData.cardType.filter(t => t !== type)
                        });
                      }
                    }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2705" />
                          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2706">
                            {type}
                          </span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2707">
                    <label className="flex items-center mb-2" data-id="element-2708">
                      <input type="checkbox" checked={formData.allMCCs} onChange={e => {
                    setFormData({
                      ...formData,
                      allMCCs: e.target.checked,
                      mccCodes: e.target.checked ? [] : formData.mccCodes
                    });
                    setShowMCCDropdown(false);
                  }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2709" />
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2710">
                        All MCCs *
                      </span>
                    </label>
                    {!formData.allMCCs && <MultiSelectDropdown label="Select MCCs" options={mockMCCCodes.map(mcc => ({
                  id: mcc.code,
                  name: `${mcc.code} - ${mcc.name}`
                }))} selectedValues={formData.mccCodes} onChange={values => setFormData({
                  ...formData,
                  mccCodes: values
                })} isOpen={showMCCDropdown} setIsOpen={setShowMCCDropdown} error={errors.mccCodes} displayKey="name" valueKey="id" data-id="element-2711" />}
                  </div>
                  <div className="md:col-span-2" data-id="element-2712">
                    <label className="flex items-center mb-2" data-id="element-2713">
                      <input type="checkbox" checked={formData.allMerchants} onChange={e => {
                    setFormData({
                      ...formData,
                      allMerchants: e.target.checked,
                      merchants: e.target.checked ? [] : formData.merchants
                    });
                    setShowMerchantDropdown(false);
                  }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-2714" />
                      <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2715">
                        All Merchants *
                      </span>
                    </label>
                    {!formData.allMerchants && <MultiSelectDropdown label="Select Merchants" options={mockMerchants} selectedValues={formData.merchants} onChange={values => setFormData({
                  ...formData,
                  merchants: values
                })} isOpen={showMerchantDropdown} setIsOpen={setShowMerchantDropdown} error={errors.merchants} displayKey="name" valueKey="id" data-id="element-2716" />}
                  </div>
                </div>
              </div>
              {/* Discount Configuration */}
              <div data-id="element-2717">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-2718">
                  Discount Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2719">
                  <div data-id="element-2720">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2721">
                      Discount Type
                    </label>
                    <div className="flex space-x-4" data-id="element-2722">
                      <label className="flex items-center" data-id="element-2723">
                        <input type="radio" value="Percentage" checked={formData.discountType === 'Percentage'} onChange={e => setFormData({
                      ...formData,
                      discountType: e.target.value
                    })} className="text-blue-600 focus:ring-blue-500" data-id="element-2724" />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2725">
                          Percentage
                        </span>
                      </label>
                      <label className="flex items-center" data-id="element-2726">
                        <input type="radio" value="Flat" checked={formData.discountType === 'Flat'} onChange={e => setFormData({
                      ...formData,
                      discountType: e.target.value
                    })} className="text-blue-600 focus:ring-blue-500" data-id="element-2727" />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-2728">
                          Flat
                        </span>
                      </label>
                    </div>
                  </div>
                  <div data-id="element-2729">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2730">
                      Discount Value *
                    </label>
                    <div className="relative" data-id="element-2731">
                      <input type="number" value={formData.discountValue} onChange={e => setFormData({
                    ...formData,
                    discountValue: parseFloat(e.target.value) || 0
                  })} className={`w-full rounded-md border ${errors.discountValue ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2732" />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-2733">
                        <span className="text-gray-500 sm:text-sm" data-id="element-2734">
                          {formData.discountType === 'Percentage' ? '%' : 'PKR'}
                        </span>
                      </div>
                    </div>
                    {errors.discountValue && <p className="mt-1 text-xs text-red-600" data-id="element-2735">
                        {errors.discountValue}
                      </p>}
                  </div>
                  {formData.discountType === 'Percentage' && <div data-id="element-2736">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2737">
                        Max Cap Per Transaction (PKR) *
                      </label>
                      <input type="number" value={formData.maxCap} onChange={e => setFormData({
                  ...formData,
                  maxCap: parseFloat(e.target.value) || 0
                })} className={`w-full rounded-md border ${errors.maxCap ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-800 py-2 px-3 text-sm`} data-id="element-2738" />
                      {errors.maxCap && <p className="mt-1 text-xs text-red-600" data-id="element-2739">
                          {errors.maxCap}
                        </p>}
                    </div>}
                  <div data-id="element-2740">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2741">
                      Minimum Transaction Amount (PKR)
                    </label>
                    <input type="number" value={formData.minTransaction} onChange={e => setFormData({
                  ...formData,
                  minTransaction: parseFloat(e.target.value) || 0
                })} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2742" />
                  </div>
                  <div data-id="element-2743">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2744">
                      Max Transactions per Customer
                    </label>
                    <input type="number" value={formData.maxTransactionsPerCustomer} onChange={e => setFormData({
                  ...formData,
                  maxTransactionsPerCustomer: parseInt(e.target.value) || 0
                })} placeholder="0 for unlimited" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2745" />
                  </div>
                  <div data-id="element-2746">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2747">
                      Max Discount per Customer (PKR)
                    </label>
                    <input type="number" value={formData.maxDiscountPerCustomer} onChange={e => setFormData({
                  ...formData,
                  maxDiscountPerCustomer: parseFloat(e.target.value) || 0
                })} placeholder="0 for unlimited" className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm" data-id="element-2748" />
                    <p className="mt-1 text-xs text-gray-500" data-id="element-2749">
                      Total maximum discount amount a customer can avail
                    </p>
                  </div>
                </div>
              </div>
              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2750">
                <button onClick={() => setShowAddEditModal(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2751">
                  Cancel
                </button>
                <button onClick={() => {
              if (validateForm()) {
                setShowPreview(true);
              }
            }} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2752">
                  Preview & Validate
                </button>
              </div>
            </> : <>
              {/* Preview */}
              <div data-id="element-2753">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-2754">
                  Discount Summary
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-6" data-id="element-2755">
                  <div className="flex items-start" data-id="element-2756">
                    <div className="flex-shrink-0" data-id="element-2757">
                      <PercentIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-2758" />
                    </div>
                    <div className="ml-3 flex-1" data-id="element-2759">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-2760">
                        {formData.name}
                      </h4>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400" data-id="element-2761">
                        {formData.description}
                      </p>
                      <div className="mt-4 grid grid-cols-2 gap-4" data-id="element-2762">
                        <div data-id="element-2763">
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2764">
                            Discount
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2765">
                            {formatDiscountDisplay(formData)}
                          </p>
                        </div>
                        <div data-id="element-2766">
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2767">
                            Valid Period
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2768">
                            {formatDate(formData.effectiveDate)} -{' '}
                            {formatDate(formData.endDate)}
                          </p>
                        </div>
                        <div data-id="element-2769">
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2770">
                            Applicable Schemes
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2771">
                            {formData.scheme.join(', ')}
                          </p>
                        </div>
                        <div data-id="element-2772">
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2773">
                            Applicable Banks
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2774">
                            {formData.allBanks ? 'All Banks' : formData.bank.join(', ')}
                          </p>
                        </div>
                        <div data-id="element-2775">
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2776">
                            Channel
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2777">
                            {formData.channel}
                          </p>
                        </div>
                        {formData.minTransaction > 0 && <div data-id="element-2778">
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2779">
                              Min Transaction
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2780">
                              {formatCurrency(formData.minTransaction)}
                            </p>
                          </div>}
                        {formData.maxDiscountPerCustomer > 0 && <div data-id="element-2781">
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2782">
                              Max Discount per Customer
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2783">
                              {formatCurrency(formData.maxDiscountPerCustomer)}
                            </p>
                          </div>}
                        {formData.binRange && <div data-id="element-2784">
                            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2785">
                              BINs
                            </p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2786">
                              {formData.binRange}
                            </p>
                          </div>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-4" data-id="element-2787">
                  <div className="flex" data-id="element-2788">
                    <div className="flex-shrink-0" data-id="element-2789">
                      <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-2790" />
                    </div>
                    <div className="ml-3" data-id="element-2791">
                      <h3 className="text-sm font-medium text-green-800 dark:text-green-300" data-id="element-2792">
                        Configuration Validated
                      </h3>
                      <p className="mt-1 text-sm text-green-700 dark:text-green-400" data-id="element-2793">
                        All fields have been validated successfully. You can now
                        save this discount.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Preview Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-2794">
                <button onClick={() => setShowPreview(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2795">
                  Back to Edit
                </button>
                <button onClick={handleSave} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700" data-id="element-2796">
                  Save Discount
                </button>
              </div>
            </>}
        </div>
      </Modal>
    </div>;
};
export default DiscountManagement;